import { Outlet } from 'react-router-dom';
import { FiShield, FiEdit2 } from 'react-icons/fi';
import { FaPlus, FaMinus } from 'react-icons/fa';
import { IoIosRefresh, IoMdArrowRoundBack } from 'react-icons/io';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import NewTooltip from '../../atoms/NewTooltip';
import DebtorContactModal from './DraweeValidationPageForm';
import ConfirmationStatus from './ConfirmationStatus';
import { COLLATERAL_ROUTES, formatCurrency, formatDate, formatCpfCnpj } from './collateralUtils';
import SummaryCard from './SummaryCard';
import useReceivablePageList from '../../../hooks/useReceivablePageList';

const DV_STATUS_LABELS: Record<string, string> = {
    NOT_STARTED: 'Não iniciado', IN_PROGRESS: 'Em validação',
    CONFIRMED: 'Validado', PARTIALLY_CONFIRMED: 'Validado parcial', REJECTED: 'Rejeitado',
};
const DV_STATUS_COLORS: Record<string, string> = {
    NOT_STARTED: 'bg-gray-500 text-white', IN_PROGRESS: 'bg-yellow-500 text-white',
    CONFIRMED: 'bg-green-500 text-white', PARTIALLY_CONFIRMED: 'bg-orange-500 text-white', REJECTED: 'bg-red-500 text-white',
};
const DRAWEE_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
    NONE: { label: 'Não iniciado', color: 'bg-gray-500' },
    NAO_INICIADO: { label: 'Não iniciado', color: 'bg-gray-500' },
    FAST: { label: 'Rápido', color: 'bg-green-500' },
    WAITING: { label: 'Aguardando retorno', color: 'bg-purple-500' },
    MODERATELY_FAST: { label: 'Moderadamente rápido', color: 'bg-yellow-500' },
    LONG_TIME: { label: 'Demora responder', color: 'bg-orange-500' },
    NO_RESPOND: { label: 'Não responde', color: 'bg-red-500' },
};

const columns = [
    { id: 'debtor', title: 'Sacado', field: 'debtor', canSort: true },
    { id: 'doc', title: 'CPF/CNPJ', field: 'doc', canSort: true, width: '150px' },
    { id: 'titles', title: 'Títulos', field: 'titles', canSort: true, cssClass: 'text-center', width: '70px' },
    { id: 'parcela', title: 'Parcela', canSort: false, cssClass: 'text-center', width: '70px' },
    { id: 'amount', title: 'Valor NF', field: 'amount', canSort: true, cssClass: 'text-right', width: '130px' },
    { id: 'validated', title: 'Valor Validado', field: 'validated', canSort: true, cssClass: 'text-right', width: '130px' },
    { id: 'status', title: 'Status', field: 'status', canSort: true, cssClass: 'text-center', width: '160px' },
    { id: 'actions', title: '', canSort: false, width: '40px', cssClass: 'text-center' },
];

const ReceivablePageList = () => {
    const {
        validationId, navigate,
        isLoading, initialLoading, apiInfo, apiData,
        statusFilter, setStatusFilter,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        expanded, toggleExpand,
        selectedDebtor, selectDebtor, closeDebtorPanel,
        groups, filteredGroups, statusCounts, totals,
        refresh,
    } = useReceivablePageList();

    const filterGroups = [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={() => setStatusFilter('')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!statusFilter ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{groups.length}</span>
                        </div>
                    </button>
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">STATUS</div>
                        <div className="space-y-1">
                            {Object.entries(statusCounts).sort(([a], [b]) => a.localeCompare(b)).map(([s, count]) => (
                                <button key={s} onClick={() => setStatusFilter(s)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === s ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-3 h-3 rounded-full ${(DV_STATUS_COLORS[s] || '').split(' ')[0]}`} />
                                            <span>{DV_STATUS_LABELS[s] || s}</span>
                                        </div>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{count}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 px-3">RESUMO</div>
                        <div className="px-3 space-y-3">
                            <SummaryCard label="Sacados" total={totals.uniqueDebtors} validated={totals.validatedDebtors} />
                            <SummaryCard label="Títulos" total={totals.count} validated={totals.confirmedTitles} />
                            <SummaryCard label="Valor Total" total={totals.amount} validated={totals.validated} isCurrency />
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    const tableRows: JSX.Element[] = [];
    filteredGroups.forEach((g, gIdx) => {
        const isExpanded = expanded.has(g.key);
        const draweeConfig = DRAWEE_STATUS_CONFIG[g.draweeStatus || 'NONE'] || DRAWEE_STATUS_CONFIG.NONE;

        tableRows.push(
            <tr key={g.key}
                className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${gIdx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'} border-b border-gray-200 dark:border-slate-700`}
                onClick={() => toggleExpand(g.key)}>
                <td className="py-2 px-3 text-sm font-medium dark:text-white max-w-0">
                    <div className="flex items-center gap-2">
                        <button className="text-gray-500 hover:text-gray-800 dark:text-gray-400 flex-shrink-0">
                            {isExpanded ? <FaMinus size={10} /> : <FaPlus size={10} />}
                        </button>
                        <span className="truncate" title={g.name}>{g.name}</span>
                        <NewTooltip content={draweeConfig.label} alignment="top">
                            <span className={`inline-block w-2 h-2 rounded-full ${draweeConfig.color}`} />
                        </NewTooltip>
                    </div>
                </td>
                <td className="py-2 px-3 text-sm dark:text-white whitespace-nowrap">{formatCpfCnpj(g.document)}</td>
                <td className="py-2 px-3 text-center text-sm dark:text-white">{g.titleCount}</td>
                <td className="py-2 px-3 text-center text-sm dark:text-white"></td>
                <td className="py-2 px-3 text-right text-sm dark:text-white">{formatCurrency(g.totalAmount)}</td>
                <td className="py-2 px-3 text-right text-sm dark:text-white">{formatCurrency(g.totalValidated || 0)}</td>
                <td className="py-2 px-3 text-center"><ConfirmationStatus status={g.status} /></td>
                <td className="py-2 px-3 text-center" onClick={e => e.stopPropagation()}>
                    <NewTooltip content="Validar ou editar validação" alignment="left">
                        <button onClick={() => selectDebtor(g)}
                            className="opacity-0 group-hover:opacity-100 text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-opacity">
                            <FiEdit2 size={14} />
                        </button>
                    </NewTooltip>
                </td>
            </tr>
        );

        if (isExpanded) {
            tableRows.push(
                <tr key={`${g.key}-detail`}>
                    <td colSpan={8} className="p-0 bg-slate-50 dark:bg-slate-900/50">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-slate-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400">
                                    <th className="py-1.5 px-3 text-left font-medium">Nº NF</th>
                                    <th className="py-1.5 px-3 text-left font-medium">Chave NFe</th>
                                    <th className="py-1.5 px-2 text-center font-medium">Parc.</th>
                                    <th className="py-1.5 px-2 text-center font-medium">Emissão</th>
                                    <th className="py-1.5 px-2 text-center font-medium">Venc.</th>
                                    <th className="py-1.5 px-2 text-left font-medium">Cidade/UF</th>
                                    <th className="py-1.5 px-3 text-right font-medium">Valor</th>
                                    <th className="py-1.5 px-3 text-center font-medium">Status</th>
                                    <th className="py-1.5 px-3 text-left font-medium">Observação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {g.items.map((r, rIdx) => (
                                    <tr key={r.id} className={rIdx % 2 === 0 ? 'bg-white dark:bg-slate-800/50' : 'bg-slate-50 dark:bg-slate-900/30'}>
                                        <td className="py-1.5 px-3 text-gray-700 dark:text-gray-300">{r.titleNumber}</td>
                                        <td className="py-1.5 px-3 text-gray-500 dark:text-gray-400 font-mono text-[10px] max-w-[200px] truncate" title={r.nfeKey}>{r.nfeKey}</td>
                                        <td className="py-1.5 px-2 text-center text-gray-600 dark:text-gray-300">{r.receivableNumber}</td>
                                        <td className="py-1.5 px-2 text-center text-gray-600 dark:text-gray-300">{formatDate(r.issueDate || '')}</td>
                                        <td className="py-1.5 px-2 text-center text-gray-600 dark:text-gray-300">{formatDate(r.dueDate || '')}</td>
                                        <td className="py-1.5 px-2 text-gray-600 dark:text-gray-300">{[r.city, r.state].filter(Boolean).join('/') || '—'}</td>
                                        <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">{formatCurrency(r.amount)}</td>
                                        <td className="py-1.5 px-3 text-center">
                                            {r.status && (
                                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${r.status === 'CONFIRMED' ? 'bg-green-100 text-green-700' : r.status === 'NOT_CONFIRMED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                                                    {r.status === 'CONFIRMED' ? 'Confirmado' : r.status === 'NOT_CONFIRMED' ? 'Não confirmado' : 'Pendente'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-1.5 px-3 text-gray-400 italic max-w-[150px] truncate" title={r.observation || ''}>{r.observation || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </td>
                </tr>
            );
        }
    });

    return (
        <>
            {isLoading && <Loading />}
            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' : apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={initialLoading ? [] : filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={!!selectedDebtor}
                onCloseRightPanel={closeDebtorPanel}
                rightPanel={selectedDebtor ? (
                    <DebtorContactModal
                        show={true}
                        onClose={closeDebtorPanel}
                        analysisId={validationId || ''}
                        draweeConfirmationId={selectedDebtor.draweeConfirmationId || ''}
                        draweeExternalId={selectedDebtor.draweeExternalId}
                        debtorName={selectedDebtor.name}
                        debtorDocument={selectedDebtor.document}
                        debtorStatus={selectedDebtor.validationStatus}
                        draweeStatus={selectedDebtor.draweeStatus}
                        receivables={selectedDebtor.items || []}
                        operationCode={(apiData?.['operationCode'] as string) || ''}
                        history={selectedDebtor.history || []}
                        onRefresh={refresh}
                    />
                ) : null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <button onClick={() => navigate(COLLATERAL_ROUTES.draweeConfirmation)}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                                    <IoMdArrowRoundBack size={18} />
                                </button>
                                <div>
                                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Recebíveis</h1>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{(apiData?.['operationCode'] as string) || ''} — {(apiData?.['assignor'] as string) || ''}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button onClick={refresh}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-80">
                                    <TextEdit label="Buscar sacado ou CPF/CNPJ..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && setSearch(searchInput)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder} onSort={(f, o) => { setSortField(f); setSortOrder(o); }}>
                                {tableRows.length === 0 ? (
                                    <tr><td colSpan={8} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum recebível encontrado</td></tr>
                                ) : tableRows}
                            </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={0} totalPages={1} itemsPerPage={100} totalElements={filteredGroups.length} onPageChange={() => { }} itemsLabel="sacados" />
                    </div>
                </div>
            </ThreeColumnLayout>

            <Outlet context={{ onDataChange: refresh }} />
        </>
    );
};

export default ReceivablePageList;
